/*
 * Glamoura custom components for Dawn.
 * gl-carousel, gl-compare, gl-cart-add, gl-delivery-estimate, gl-sticky-atc
 */
(() => {
  const SCROLL_DEBOUNCE_MS = 80;
  const MS_PER_SECOND = 1000;
  const WEEKEND_DAYS = [0, 6];

  const debounce = (fn, wait) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  class GlCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      this.dotsContainer = this.querySelector('[data-dots]');
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', debounce(() => this.updateDots(), SCROLL_DEBOUNCE_MS), { passive: true });
      this.buildDots();
      this.resizeObserver = new ResizeObserver(debounce(() => this.buildDots(), SCROLL_DEBOUNCE_MS));
      this.resizeObserver.observe(this.track);
      this.startAutoplay();
    }

    disconnectedCallback() {
      clearInterval(this.autoplayTimer);
      this.resizeObserver?.disconnect();
    }

    slideWidth() {
      const first = this.slides[0];
      if (!first) return 0;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      return first.getBoundingClientRect().width + gap;
    }

    pageCount() {
      const width = this.slideWidth();
      if (!width) return 1;
      const visible = Math.max(1, Math.round(this.track.clientWidth / width));
      return Math.max(1, this.slides.length - visible + 1);
    }

    currentIndex() {
      const width = this.slideWidth();
      return width ? Math.round(this.track.scrollLeft / width) : 0;
    }

    goTo(index) {
      const last = this.pageCount() - 1;
      const target = index > last ? 0 : index < 0 ? last : index;
      this.track.scrollTo({ left: target * this.slideWidth(), behavior: 'smooth' });
    }

    step(direction) {
      this.goTo(this.currentIndex() + direction);
    }

    buildDots() {
      if (!this.dotsContainer) return;
      const count = this.pageCount();
      this.dotsContainer.replaceChildren(
        ...Array.from({ length: count }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'gl-carousel__dot';
          dot.setAttribute('aria-label', `${index + 1} / ${count}`);
          dot.addEventListener('click', () => this.goTo(index));
          return dot;
        })
      );
      const isScrollable = count > 1;
      this.dotsContainer.hidden = !isScrollable;
      this.querySelectorAll('[data-prev], [data-next]').forEach((arrow) => (arrow.hidden = !isScrollable));
      this.updateDots();
    }

    updateDots() {
      if (!this.dotsContainer) return;
      const active = this.currentIndex();
      Array.from(this.dotsContainer.children).forEach((dot, index) => {
        dot.setAttribute('aria-current', index === active ? 'true' : 'false');
      });
    }

    startAutoplay() {
      const seconds = Number(this.dataset.autoplay);
      if (!seconds || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const tick = () => {
        if (!this.matches(':hover, :focus-within')) this.step(1);
      };
      this.autoplayTimer = setInterval(tick, seconds * MS_PER_SECOND);
    }
  }

  class GlCompare extends HTMLElement {
    connectedCallback() {
      const input = this.querySelector('input[type="range"]');
      if (!input) return;
      const update = () => this.style.setProperty('--gl-compare-position', `${input.value}%`);
      input.addEventListener('input', update);
      update();
    }
  }

  /*
   * Adds one or more variants to the cart and refreshes Dawn's cart drawer.
   * Variants come from data-items='[{"id":1,"quantity":1}]' or from checked
   * inputs named "gl-item" inside the closest [data-gl-cart-scope].
   */
  class GlCartAdd extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.errorBox = this.querySelector('[data-error]');
      this.button?.addEventListener('click', () => this.add());
    }

    collectItems() {
      if (this.dataset.items) return JSON.parse(this.dataset.items);
      const scope = this.closest('[data-gl-cart-scope]');
      if (!scope) return [];
      return Array.from(scope.querySelectorAll('input[name="gl-item"]:checked')).map((input) => ({
        id: Number(input.value),
        quantity: Number(input.dataset.quantity || 1),
      }));
    }

    async add() {
      if (this.isLoading) return;
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
      this.setLoading(true);
      try {
        const items = this.collectItems().filter((item) => item.id);
        if (!items.length) throw new Error(this.dataset.emptyMessage || 'Válassz legalább egy terméket.');
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || response.statusText);
        if (this.dataset.skipCart === 'true') {
          window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
          return;
        }
        if (!cart) {
          window.location.href = window.routes.cart_url;
          return;
        }
        cart.classList.remove('is-empty');
        cart.renderContents(data);
      } catch (error) {
        console.error('gl-cart-add failed', error);
        this.showError(error.message);
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      this.isLoading = isLoading;
      if (!this.button) return;
      this.button.toggleAttribute('aria-disabled', isLoading);
      this.button.classList.toggle('loading', isLoading);
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorBox.textContent = message;
      this.errorBox.hidden = !message;
    }
  }

  /* Replaces [start_date] / [end_date] with business-day delivery dates. */
  class GlDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const days = (this.dataset.days || '').split(',').map((label) => label.trim());
      const months = (this.dataset.months || '').split(',').map((label) => label.trim());
      const start = this.addBusinessDays(new Date(), Number(this.dataset.min || 0));
      const end = this.addBusinessDays(new Date(), Number(this.dataset.max || 0));
      const format = (date) => this.format(date, days, months);
      this.querySelectorAll('[data-start-date]').forEach((node) => (node.textContent = format(start)));
      this.querySelectorAll('[data-end-date]').forEach((node) => (node.textContent = format(end)));
    }

    addBusinessDays(from, count) {
      const date = new Date(from);
      let remaining = count;
      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        if (!WEEKEND_DAYS.includes(date.getDay())) remaining -= 1;
      }
      return date;
    }

    format(date, days, months) {
      // Labels are listed Monday-first, JavaScript's getDay() is Sunday-first.
      const dayName = days[(date.getDay() + 6) % 7] || '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      switch (this.dataset.format) {
        case 'mm_dd':
          return `${month}.${day}.`;
        case 'day_month_dd':
          return `${dayName}, ${months[date.getMonth()] || month} ${date.getDate()}.`;
        default:
          return `${dayName} (${month}.${day}.)`;
      }
    }
  }

  /* Shows a bar once the main add-to-cart button scrolls out of view. */
  class GlStickyAtc extends HTMLElement {
    connectedCallback() {
      // Theme animations put a transform on the product column, which would trap
      // position: fixed inside it, so the bar moves itself to <body>.
      if (this.parentElement !== document.body) {
        document.querySelectorAll(`body > gl-sticky-atc[data-form-id="${this.dataset.formId}"]`).forEach((old) => old.remove());
        document.body.appendChild(this);
        return;
      }
      this.inert = true;
      const form = document.getElementById(this.dataset.formId);
      this.mainButton = form?.querySelector('[type="submit"]');
      this.querySelector('[data-sticky-submit]')?.addEventListener('click', () => this.submit());
      if (!this.mainButton) return;
      if (this.dataset.displayWhen === 'always') {
        this.setVisible(true);
        return;
      }
      this.observer = new IntersectionObserver(([entry]) => {
        this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
      });
      this.observer.observe(this.mainButton);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event.data));
      }
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.unsubscribe?.();
    }

    onVariantChange({ html, variant }) {
      const fresh = html?.querySelector(`gl-sticky-atc[data-form-id="${this.dataset.formId}"] [data-sticky-info]`);
      const current = this.querySelector('[data-sticky-info]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
      const button = this.querySelector('[data-sticky-submit]');
      if (button) button.disabled = !variant || !variant.available;
    }

    setVisible(isVisible) {
      this.classList.toggle('is-visible', isVisible);
      this.setAttribute('aria-hidden', String(!isVisible));
      this.inert = !isVisible;
    }

    submit() {
      if (this.dataset.function === 'scroll' || !this.mainButton) {
        this.mainButton?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.mainButton.click();
    }
  }

  /* Keeps the bundle total in sync with the ticked products. Prices are in cents. */
  class GlBundle extends HTMLElement {
    connectedCallback() {
      const currency = window.Shopify?.currency?.active || 'HUF';
      this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'hu', {
        style: 'currency',
        currency,
        minimumFractionDigits: currency === 'HUF' ? 0 : 2,
        maximumFractionDigits: currency === 'HUF' ? 0 : 2,
      });
      this.addEventListener('change', () => this.update());
      this.update();
    }

    update() {
      const checked = Array.from(this.querySelectorAll('input[name="gl-item"]:checked'));
      const sum = (key) => checked.reduce((total, input) => total + Number(input.dataset[key] || 0), 0);
      const total = sum('price');
      const compare = sum('compare');
      const totalNode = this.querySelector('[data-total]');
      const compareNode = this.querySelector('[data-total-compare]');
      if (totalNode) totalNode.textContent = this.formatter.format(total / 100);
      if (compareNode) compareNode.textContent = compare > total ? this.formatter.format(compare / 100) : '';
    }
  }

  const define = (name, element) => {
    if (!customElements.get(name)) customElements.define(name, element);
  };
  define('gl-carousel', GlCarousel);
  define('gl-compare', GlCompare);
  define('gl-cart-add', GlCartAdd);
  define('gl-delivery-estimate', GlDeliveryEstimate);
  define('gl-sticky-atc', GlStickyAtc);
  define('gl-bundle', GlBundle);
})();
